import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageUpload({ value, onChange, testId = "image-upload" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large (max 8MB)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND_URL}${r.data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 border border-white/10 bg-neutral-900 overflow-hidden shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-600 text-[9px] small-caps text-center px-1">No image</div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            data-testid={`${testId}-btn`}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-[#F5B041]/60 h-10 px-4 small-caps text-[10px] transition disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {busy ? "Uploading…" : "Upload image"}
          </button>
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an image URL"
            data-testid={`${testId}-url`}
            className="w-full rounded-none bg-transparent border border-white/10 h-9 px-3 text-sm focus:border-[#C04000] outline-none"
          />
        </div>
        <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
      </div>
    </div>
  );
}
